import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import Images from "../../utils/Images";

const Hero = ({ isAuthenticated = false, user = null }) => {
  // Carousel state
  const [leftCurrentIndex, setLeftCurrentIndex] = useState(0);
  const [rightCurrentIndex, setRightCurrentIndex] = useState(0);

  // Auto-rotate carousels
  useEffect(() => {
    const leftInterval = setInterval(() => {
      setLeftCurrentIndex((prev) => (prev + 1) % leftCarouselData.length);
    }, 4000);

    const rightInterval = setInterval(() => {
      setRightCurrentIndex((prev) => (prev + 1) % rightCarouselData.length);
    }, 5000);

    return () => {
      clearInterval(leftInterval);
      clearInterval(rightInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Left carousel data - each with unique color schemes
  const leftCarouselData = [
    {
      id: 1,
      title: "Smart Speakers & Audio",
      subtitle:
        "Discover a range of smart speakers and audio devices for your home.",
      description: "Experience room-filling sound in compact designs",
      tag: "TRENDING",
      tagColor: "bg-blue-500",
      bgColor: "bg-gradient-to-br from-green-50 to-emerald-100",
      textColor: "text-gray-800",
      image: Images.beatbox,
      price: null,
    },
    {
      id: 2,
      title: "Wireless Earbuds",
      subtitle: "Premium wireless earbuds with noise cancellation technology.",
      description: "Superior audio quality for music lovers on the go",
      tag: "FEATURED",
      tagColor: "bg-purple-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-100",
      textColor: "text-gray-800",
      image: Images.beatbox,
      price: null,
    },
    {
      id: 3,
      title: "Smartwatches & Wearables",
      subtitle:
        "Advanced health monitoring and fitness tracking technology.",
      description: "Track your fitness goals and stay connected all day",
      tag: "POPULAR",
      tagColor: "bg-orange-500",
      bgColor: "bg-gradient-to-br from-orange-50 to-yellow-100",
      textColor: "text-gray-800",
      image: Images.beatbox,
      price: null,
    },
  ];

  // Right carousel data - each with unique color schemes
  const rightCarouselData = [
    {
      id: 1,
      title: "Flagship Smartphones",
      subtitle:
        "High-performance phones with advanced features and specifications.",
      description: "Top-tier performance with cutting-edge camera technology",
      tag: "HOT DEALS",
      tagColor: "bg-yellow-400",
      bgColor: "bg-gradient-to-br from-gray-900 to-gray-800",
      textColor: "text-white",
      image: Images.phones,
      price: null,
    },
    {
      id: 2,
      title: "Premium Mobile Devices",
      subtitle: "Revolutionary camera systems and powerful processors.",
      description: "Ultimate mobile photography and productivity experiences",
      tag: "LATEST",
      tagColor: "bg-blue-500",
      bgColor: "bg-gradient-to-br from-blue-900 to-indigo-900",
      textColor: "text-white",
      image: Images.phones,
      price: null,
    },
    {
      id: 3,
      title: "High-End Smartphones",
      subtitle: "Flagship performance with premium displays and fast charging.",
      description: "Speed meets elegance in these premium devices",
      tag: "BEST SELLERS",
      tagColor: "bg-red-500",
      bgColor: "bg-gradient-to-br from-red-900 to-pink-900",
      textColor: "text-white",
      image: Images.phones,
      price: null,
    },
  ];

  // Brand carousel data - COMMENTED OUT UNTIL WE HAVE OFFICIAL PARTNERSHIPS
  // Uncomment this when reactivating the brand carousel section below
  /* const brandCarouselData = [
    {
      id: 1,
      name: "Compu Ghana",
      image: Images.compu_ghana,
      description: "Your trusted tech partner",
    },
    {
      id: 2,
      name: "Melcom",
      image: Images.melcom,
      description: "Everything for everyone",
    },
    {
      id: 3,
      name: "Samsung",
      image: Images.samsung,
      description: "Innovation that excites",
    },
    {
      id: 4,
      name: "Tecno",
      image: Images.techno,
      description: "Stop at nothing",
    },
    {
      id: 5,
      name: "Jumia",
      image: Images.jumia_logo,
      description: "Africa's leading e-commerce",
    },
  ]; */

  return (
    <section className="overflow-hidden relative pt-20 min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div
          className="absolute top-0 left-0 w-full h-full opacity-30"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f3f4f6' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="relative z-10 px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        {/* Personalized Welcome Message for Authenticated Users */}

        {/* Two Side-by-Side Carousels */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 gap-8 mb-12 lg:grid-cols-2"
        >
          {/* Left Carousel - Apple HomePod Mini Style */}
          <div className="hidden space-y-4 lg:block">
            <div className="relative">
              <motion.div
                key={leftCurrentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className={`relative h-72 rounded-2xl shadow-lg overflow-hidden ${leftCarouselData[leftCurrentIndex].bgColor}`}
              >
                <div className="flex h-full">
                  {/* Content Section */}
                  <div className="flex flex-col flex-1 justify-between p-6">
                    <div>
                      <div
                        className={`inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-2 ${leftCarouselData[leftCurrentIndex].tagColor}`}
                      >
                        {leftCarouselData[leftCurrentIndex].tag}
                      </div>
                      <h3
                        className={`text-4xl font-bold ${leftCarouselData[leftCurrentIndex].textColor} mb-2`}
                      >
                        {leftCarouselData[leftCurrentIndex].title}
                      </h3>
                      <p
                        className={`text-lg ${leftCarouselData[leftCurrentIndex].textColor} opacity-80 mb-2`}
                      >
                        {leftCarouselData[leftCurrentIndex].subtitle}
                      </p>
                      <p
                        className={`text-base ${leftCarouselData[leftCurrentIndex].textColor} opacity-70 italic`}
                      >
                        {leftCarouselData[leftCurrentIndex].description}
                      </p>
                    </div>
                  </div>

                  {/* Image Section */}
                  <div className="flex justify-center items-center p-6 w-56 h-full">
                    <img
                      src={leftCarouselData[leftCurrentIndex].image}
                      alt={leftCarouselData[leftCurrentIndex].title}
                      className="object-contain w-full h-full opacity-80"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Left Carousel Indicators */}
              <div className="flex justify-center mt-4 space-x-2">
                {leftCarouselData.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                      index === leftCurrentIndex ? "bg-gray-800" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Carousel - Xiaomi Mi 11 Ultra Style */}
          <div className="space-y-4">
            <div className="relative">
              <motion.div
                key={rightCurrentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className={`relative h-56 lg:h-72 rounded-2xl shadow-lg overflow-hidden ${rightCarouselData[rightCurrentIndex].bgColor}`}
              >
                <div className="flex h-full">
                  {/* Content Section */}
                  <div className="flex flex-col flex-1 justify-between p-4 lg:p-6">
                    <div>
                      <div className="mb-2">
                        <div
                          className={`inline-block px-2 lg:px-3 py-1 rounded-full text-xs font-bold text-black ${rightCarouselData[rightCurrentIndex].tagColor}`}
                        >
                          {rightCarouselData[rightCurrentIndex].tag}
                        </div>
                      </div>
                      <h3
                        className={`text-2xl lg:text-4xl font-bold ${rightCarouselData[rightCurrentIndex].textColor} mb-2`}
                      >
                        {rightCarouselData[rightCurrentIndex].title}
                      </h3>
                      <p
                        className={`text-sm lg:text-lg ${rightCarouselData[rightCurrentIndex].textColor} opacity-80 mb-2`}
                      >
                        {rightCarouselData[rightCurrentIndex].subtitle}
                      </p>
                      <p
                        className={`text-xs lg:text-base ${rightCarouselData[rightCurrentIndex].textColor} opacity-70 italic`}
                      >
                        {rightCarouselData[rightCurrentIndex].description}
                      </p>
                    </div>
                  </div>

                  {/* Image Section */}
                  <div className="flex justify-center items-center p-4 w-40 h-full lg:p-6 lg:w-56">
                    <img
                      src={rightCarouselData[rightCurrentIndex].image}
                      alt={rightCarouselData[rightCurrentIndex].title}
                      className="object-contain w-full h-full opacity-80"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Right Carousel Indicators */}
              <div className="flex justify-center mt-4 space-x-2">
                {rightCarouselData.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                      index === rightCurrentIndex
                        ? "bg-gray-800"
                        : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Brand Carousel Section - COMMENTED OUT UNTIL WE HAVE OFFICIAL PARTNERSHIPS */}
        {/* <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 mb-8"
        >
          <div className="mb-8 text-left">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              Browse by Brands
            </h2>
            <p className="text-lg text-left text-gray-600">
              Discover amazing deals from our trusted retail brands across Ghana
            </p>
          </div>

          <div className="overflow-hidden">
            <motion.div
              className="flex gap-8 py-4"
              animate={{ x: [0, -100, 0] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              {[...brandCarouselData, ...brandCarouselData].map(
                (brand, index) => (
                  <motion.div
                    key={`brand-${index}`}
                    className="flex-shrink-0 w-32 h-24 rounded-2xl shadow-sm"
                  >
                    <div className="flex justify-center items-center w-full h-full bg-white rounded-2xl shadow-lg">
                      <img
                        src={brand.image}
                        alt={brand.name}
                        className="object-contain w-16 h-16 opacity-80"
                      />
                    </div>
                  </motion.div>
                )
              )}
            </motion.div>
          </div>
        </motion.div> */}

        {/* Why Choose Us Section - Temporary replacement for brand section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 mb-8"
        >
          <div className="mb-8 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              Why Choose Dealboxx?
            </h2>
            <p className="text-lg text-gray-600">
              Your trusted platform for discovering the best deals
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Feature 1 */}
            <motion.div
              whileHover={{ y: -5 }}
              className="p-6 bg-white rounded-2xl shadow-lg transition-shadow hover:shadow-xl"
            >
              <div className="flex justify-center items-center mb-4 w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-800">Best Deals</h3>
              <p className="text-sm text-gray-600">
                Access exclusive offers and competitive prices on quality products
              </p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              whileHover={{ y: -5 }}
              className="p-6 bg-white rounded-2xl shadow-lg transition-shadow hover:shadow-xl"
            >
              <div className="flex justify-center items-center mb-4 w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-800">Secure Platform</h3>
              <p className="text-sm text-gray-600">
                Shop with confidence on our safe and reliable platform
              </p>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              whileHover={{ y: -5 }}
              className="p-6 bg-white rounded-2xl shadow-lg transition-shadow hover:shadow-xl"
            >
              <div className="flex justify-center items-center mb-4 w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-800">Fast & Easy</h3>
              <p className="text-sm text-gray-600">
                Quick browsing and seamless experience for finding what you need
              </p>
            </motion.div>

            {/* Feature 4 */}
            <motion.div
              whileHover={{ y: -5 }}
              className="p-6 bg-white rounded-2xl shadow-lg transition-shadow hover:shadow-xl"
            >
              <div className="flex justify-center items-center mb-4 w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full">
                <svg
                  className="w-8 h-8 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-800">Quality Selection</h3>
              <p className="text-sm text-gray-600">
                Curated deals on products from trusted sources
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Product Marketplace Banner */}
    </section>
  );
};

export default Hero;
